import {useMutation} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {toast} from '@ui/toast/toast';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';

interface ReleaseLicensePayload {
  id: number;
}

export function useReleaseLicense() {
  return useMutation({
    mutationFn: (payload: ReleaseLicensePayload) => releaseLicense(payload),
    onSuccess: () => {
      toast.positive('License released successfully');
      queryClient.invalidateQueries({queryKey: ['licenses']});
    },
    onError: err => showHttpErrorToast(err),
  });
}

async function releaseLicense(
  payload: ReleaseLicensePayload,
): Promise<BackendResponse> {
  // Generate a token first
  const tokenResponse = await apiClient.post('drive/generate-token', {
    endpoint_type: 'licenses',
  });

  const token = tokenResponse.data.token;

  // Call external API directly with axios
  const response = await fetch('https://apcrosa.syscomlive.co.uk/api/v1/drive/license/release-from-user', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Token': token,
    },
    body: JSON.stringify({
      ...payload,
      use_for_drive: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Check for API error responses (status: 0 indicates error)
  if (data.status === 0) {
    throw new Error(data.msg || 'An error occurred while releasing the license');
  }

  return data;
}
